import {memo, useCallback, useEffect} from 'react';
import {useIntl} from 'react-intl';

import Constants from 'utils/constants';
import {isKeyPressed} from 'utils/utils';

import Toast from 'components/toast/toast';

import './new_replies_banner.scss';

type Props = {
    hasNewReplies: boolean;
    width: number;
    onClick: () => void;
    onDismiss: () => void;
    actions: {
        updateThreadToastStatus: (status: boolean) => void;
    };
};

function NewRepliesBanner({hasNewReplies, onClick, onDismiss, width, actions}: Props) {
    const {formatMessage} = useIntl();
    const {updateThreadToastStatus} = actions;

    useEffect(() => {
        updateThreadToastStatus(hasNewReplies);
    }, [hasNewReplies, updateThreadToastStatus]);

    const handleShortcut = useCallback<EventListener>(
        (e) => {
            if (isKeyPressed(e, Constants.KeyCodes.ESCAPE)) {
                if (hasNewReplies) {
                    onDismiss();
                }
            }
        },
        [hasNewReplies, onDismiss],
    );

    useEffect(() => {
        document.addEventListener('keydown', handleShortcut);

        return () => {
            document.removeEventListener('keydown', handleShortcut);
        };
    }, [handleShortcut]);

    return (
        <div
            className='newRepliesBanner'
            style={{width}}
        >
            <Toast
                show={hasNewReplies}
                showActions={true}
                onClick={onClick}
                onDismiss={onDismiss}
                onClickMessage={formatMessage({
                    id: 'postlist.toast.scrollToLatest',
                    defaultMessage: 'Jump to new messages',
                })}
                overlayPlacement='top'
                width={156}
                extraClasses='toastExtra'
                shrink={true}
            >
                <span className='toastMessage'>
                    {formatMessage({
                        id: 'rhs_thread.toast.newReplies',
                        defaultMessage: 'New Replies',
                    })}
                </span>
            </Toast>
        </div>
    );
}

export default memo(NewRepliesBanner);
